var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { Linking, Platform, Pressable, Text, View } from 'react-native';
import { H1View } from '../Typography/H1View';
import { H2View } from '../Typography/H2View';
import { H3View } from '../Typography/H3View';
import { H4View } from '../Typography/H4View';
import { H5View } from '../Typography/H5View';
import { H6View } from '../Typography/H6View';
import { ParagraphView } from '../Typography/Paragraph';
import { AnimatedTextView } from '../Typography/AnimatedTextView';
import { SmallView } from '../Typography/SmallView';
import { BoldView } from '../Typography/BoldView';
import { SimpleButtonView } from '../Buttons/SimpleButtonView';
import { useColorSchemeContext } from '../../Contexts/ThemeContext';
import { AnimatedImageView } from '../Image/AnimatedImageView';
import { PressableButtonView } from '../Buttons/PressableButtonView';
import { getColorScheme } from '../../Hooks/UseColorScheme';
import { convertHTMLToJSX, objectToStyle } from '../../../Helpers/HTML/HTMLParser';
function parse_dimension(value) {
    if (typeof value !== 'string') {
        return value;
    }
    if (value.endsWith('px')) {
        return Number(value.slice(0, value.length - 2));
    }
    const new_val = Number(value);
    if (!isNaN(new_val)) {
        return new_val;
    }
    return value;
}
function countTheNumberOfULParentElements(element, count = 0) {
    if (element.parent) {
        if (element.parent.name === 'ul') {
            count++;
        }
        count = countTheNumberOfULParentElements(element.parent, count);
    }
    return count;
}
const dom_elements = {
    h1: (_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        return _jsx(H1View, Object.assign({ textStyle: style }, props));
    },
    h2: (_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        return _jsx(H2View, Object.assign({ textStyle: style }, props));
    },
    h3: (_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        return _jsx(H3View, Object.assign({ textStyle: style }, props));
    },
    h4: (_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        return _jsx(H4View, Object.assign({ textStyle: style }, props));
    },
    h5: (_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        return _jsx(H5View, Object.assign({ textStyle: style }, props));
    },
    h6: (_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        return _jsx(H6View, Object.assign({ textStyle: style }, props));
    },
    p: (_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        return _jsx(ParagraphView, Object.assign({ textStyle: style }, props));
    },
    ul: (_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        return _jsx(View, Object.assign({}, props, { style: [{ marginBottom: 5 }, style] }));
    },
    ol: View,
    img: (_a) => {
        var { style, src, width, height } = _a, props = __rest(_a, ["style", "src", "width", "height"]);
        if (!style) {
            style = {};
        }
        if (width) {
            style.width = parse_dimension(width);
        }
        if (height) {
            style.height = parse_dimension(height);
        }
        return (_jsx(AnimatedImageView, { image: src, containerStyle: style }));
    },
    span: (_a) => {
        var { children, style, action, onPress } = _a, props = __rest(_a, ["children", "style", "action", "onPress"]);
        const color_scheme_context = useColorSchemeContext();
        if (action) {
            const text_style = {
                textDecorationLine: 'underline',
                textDecorationColor: color_scheme_context.scheme === 'dark'
                    ? '#fff'
                    : '#000',
                textDecorationStyle: 'solid',
            };
            return (_jsx(SimpleButtonView, { onPress: onPress, children: _jsx(SmallView, Object.assign({ textStyle: [style, text_style] }, props, { children: children })) }));
        }
        return (_jsx(SmallView, Object.assign({ textStyle: style }, props, { children: children })));
    },
    li: (_a) => {
        var { children, style, ULCount } = _a, props = __rest(_a, ["children", "style", "ULCount"]);
        const padding_start = typeof ULCount === 'number' && ULCount > 0
            ? (ULCount - 1) * 10
            : 0;
        if (!Array.isArray(children)) {
            return _jsx(View, { style: { paddingStart: padding_start }, children: _jsxs(AnimatedTextView, Object.assign({ textStyle: style }, props, { children: ["\u25CF ", children] })) });
        }
        let has_blocks = false;
        const new_children = children.map((child, i) => {
            var _a;
            if (child && typeof child === 'object') {
                if (!((_a = child.props) === null || _a === void 0 ? void 0 : _a.isTextNode)) {
                    has_blocks = true;
                }
            }
            if (i === 0) {
                return _jsxs(AnimatedTextView, Object.assign({ textStyle: style }, props, { children: ["\u25CF ", child] }), i);
            }
            if (typeof child === 'string') {
                return _jsx(AnimatedTextView, Object.assign({ textStyle: style }, props, { children: child }), i);
            }
            return child;
        });
        return has_blocks
            ? _jsx(View, { style: { paddingStart: padding_start }, children: new_children })
            : _jsx(Text, { style: { paddingStart: padding_start }, children: new_children });
    },
    order_li: (_a) => {
        var { children, style, index } = _a, props = __rest(_a, ["children", "style", "index"]);
        return _jsx(View, { children: _jsxs(AnimatedTextView, Object.assign({ textStyle: style }, props, { children: [index, ". ", children] })) });
    },
    div: (_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        if ((props.children || []).find((child) => typeof child === 'string' || typeof child === 'number')) {
            if (Platform.OS === 'web') {
                return _jsx(View, { style: { width: '100%' }, children: _jsx(SmallView, Object.assign({ textStyle: style }, props)) });
            }
            return _jsx(SmallView, Object.assign({ textStyle: style }, props));
        }
        return _jsx(View, Object.assign({}, props));
    },
    b: (_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        return _jsx(BoldView, Object.assign({ textStyle: style }, props));
    },
    strong: (_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        return _jsx(BoldView, Object.assign({ textStyle: style }, props));
    },
    small: (_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        return _jsx(SmallView, Object.assign({ textStyle: style }, props));
    },
    em: (_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        return _jsx(SmallView, Object.assign({ textStyle: [style, { fontStyle: 'italic' }] }, props));
    },
    br: (_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        if (Platform.OS === 'web') {
            return React.createElement('br');
        }
        return _jsx(Text, { children: '\n' });
    },
    a: (_a) => {
        var { children, style, target, href } = _a, props = __rest(_a, ["children", "style", "target", "href"]);
        const handlePress = () => {
            if (!href) {
                return;
            }
            return Linking.canOpenURL(href)
                .then(isOk => {
                if (!isOk) {
                    return Promise.reject(new Error(`Unable to open link ${href}!`));
                }
                return Linking.openURL(href);
            })
                .catch(console.error);
        };
        if (!style) {
            style = {};
        }
        if (href) {
            style.textDecorationLine = 'underline';
            style.textDecorationStyle = 'solid';
            style.textDecorationColor = getColorScheme().value.colors.text;
        }
        return (_jsx(Text, { children: _jsx(Pressable, { onPress: handlePress, children: _jsx(SmallView, { textStyle: style, children: children }) }) }));
    },
    button: (_a) => {
        var { children, style, onPress, size, type } = _a, props = __rest(_a, ["children", "style", "onPress", "size", "type"]);
        return (_jsx(PressableButtonView, { action: 'html', size: size, type: type || 'primary', wrapperStyle: style, text: children, onPress: typeof onPress === 'function' ? onPress : undefined }));
    }
};
const text_tags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'b', 'strong', 'small', 'em'];
const processing_instructions = ({ onPress, instructions }) => ([
    {
        shouldProcessNode: (node) => node.type === 'tag',
        processNode: (node, children, key, prev_node, next_node) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
            const props = Object.assign(Object.assign({}, node.props || {}), { key });
            if (Array.isArray(props.className) && props.className.includes('button')) {
                node.name = 'button';
            }
            if (props.action || node.name === 'button') {
                props.onPress = () => onPress === null || onPress === void 0 ? void 0 : onPress({ action: props.action });
            }
            const parent_name = ((_a = node.parent) === null || _a === void 0 ? void 0 : _a.name) || '';
            if (node.name === 'li') {
                props.ULCount = countTheNumberOfULParentElements(node);
            }
            if (parent_name === 'ol' && node.name === 'li') {
                const index = typeof key === 'number'
                    ? key + 1
                    : key;
                return React.createElement(dom_elements.order_li, Object.assign(Object.assign({}, props), { index }), children);
            }
            if (text_tags.includes(node.name)) {
                props.isTextNode = true;
                if (!props.style) {
                    props.style = {};
                }
                if (node.name === 'b') {
                    props.style = Object.assign({ fontWeight: '600' }, props.style);
                }
                else if (node.name === 'strong') {
                    props.style = Object.assign({ fontWeight: '800' }, props.style);
                }
                else if (node.name === 'em') {
                    props.italic = true;
                }
                if (text_tags.includes(parent_name) && dom_elements[parent_name]) {
                    if (!((_b = props.style) === null || _b === void 0 ? void 0 : _b.color) && ((_e = (_d = (_c = node.parent) === null || _c === void 0 ? void 0 : _c.props) === null || _d === void 0 ? void 0 : _d.style) === null || _e === void 0 ? void 0 : _e.color)) {
                        props.style.color = (_h = (_g = (_f = node.parent) === null || _f === void 0 ? void 0 : _f.props) === null || _g === void 0 ? void 0 : _g.style) === null || _h === void 0 ? void 0 : _h.color;
                    }
                    props.style = Object.assign(Object.assign({}, ((_k = (_j = node.parent) === null || _j === void 0 ? void 0 : _j.props) === null || _k === void 0 ? void 0 : _k.style) || {}), props.style);
                }
            }
            if (dom_elements[node.name]) {
                if ((prev_node === null || prev_node === void 0 ? void 0 : prev_node.name) === 'br') {
                    if (Platform.OS === 'android') {
                        return React.createElement(dom_elements[node.name], props, children);
                    }
                    return React.createElement(View, { key }, [React.createElement(dom_elements[node.name], props, children)]);
                }
                return React.createElement(dom_elements[node.name], props, children);
            }
            if ((children || []).find(child => typeof child === 'string' || typeof child === 'number')) {
                return React.createElement(Text, props, children);
            }
            return React.createElement(View, props, children);
        }
    },
    {
        shouldProcessNode: (node) => { var _a; return node.type === 'text' && !!((_a = node.data) === null || _a === void 0 ? void 0 : _a.trim()); },
        processNode: (node) => node.data
    },
    ...instructions || []
]);
export const HTMLView = ({ value, style, onPress, instructions }) => {
    if (!value) {
        return [];
    }
    const elements = useMemo(() => convertHTMLToJSX(`<div style="${objectToStyle(style)}">${value}</div>`, processing_instructions({ onPress, value, style, instructions })), [value, style, onPress, instructions]);
    return elements;
};
export function HtmlTextView({ html, style }) {
    return _jsx(HTMLView, { value: html, style: style });
}
